import React, { useCallback, useEffect, useState } from "react";
import "./Customers.css";
import { customerModelData } from "../../services/ModelService";
import { Customer } from "../../model/customer";
import useModel from "../../hooks/useModel";
import SaveMenu from "../../components/SaveMenu";
import LoadMessage from "../../components/LoadMessage";
import { Card, Col, Flex, Input, Row, Form, Typography, Button, Table, Popconfirm, Select } from "antd";
import CustomerMenu from "./CustomerMenu";
import { noop, startCase } from "lodash";
import { PlusOutlined, MinusCircleOutlined } from "@ant-design/icons";
import { Button as AntButton } from "antd";
import { AUSTRALIAN_PHONE_REGEX, EMAIL_REGEX } from "../../libs/regex";
import {DeleteOutlined} from "@ant-design/icons";
import { useHistory } from "react-router-dom";
import { FilterTags } from "../../containers/FilterTag";
import { filterItems } from "../Bookings/BookingFilters";
import dayjs from "../../dayjs";
import useAllBookings from "../Bookings/useAllBookings";
import { useBooking } from "../Bookings/BookingContext";
import { ReactComponent as Copy } from "../../assets/icons/Copy.svg";
import { ReactComponent as Trash } from "../../assets/icons/Trash.svg";
import { ReactComponent as Close } from "../../assets/icons/Close.svg";
import LoaderButton from "../../components/LoaderButton";
import { ulid } from "ulid";
import { charterModelData, routeModelData, shiftBatModelData } from "../../services/ModelService";
import { CHARTER_STATUS } from "../../model/charter";
import { useAppContext } from "../../libs/contextLib";

const { TextArea } = Input;
const {Paragraph, Title} = Typography;

const validateAustralianPhoneNumber = (rule, value) => {
  // remove spaces and parentheses
  const formattedNumber = value.replace(/\s+/g, '').replace(/\(/g, '').replace(/\)/g, ''); 
  const valid = AUSTRALIAN_PHONE_REGEX.test(formattedNumber);
  if (!valid) {
    return Promise.reject(new Error("Please enter a valid australian phone number"));
  }
  return Promise.resolve();
};

export const CustomerDetailsCard = ({ mode, customer, setCustomerWith, form, operator }) => {
  // Initialize form values when customer data changes
  useEffect(() => {
    if (mode.edit) {
      form?.setFieldsValue(customer);
    }
  }, [customer, mode.edit, form]);

  const handleChange = async (field, value) => {
    setCustomerWith(field, value);
    // Validate the specific field
    try {
      await form.validateFields([field]);
      // Only clear errors if validation passes
      form?.setFields([{ name: field, errors: [] }]);
    } catch (error) {
      // Keep the error message if validation fails
      console.log('Validation failed for field:', field);
    }
  };

  return (
    <Form form={form} layout="vertical" validateTrigger="onSubmit">
      <Row gutter={[30, 30]}>
        <Col xs={24} lg={12}>
          <Form.Item label="Name" name="name" initialValue={customer.name} rules={[{ required: true, message: "Please enter the name" }]}>
            {mode.edit ? (
              <Input
                type="text"
                placeholder="Enter name"
                value={customer.name}
                onChange={({ target: { value } }) => handleChange("name", value)}
              />
            ) : (
              <strong>{startCase(customer.name)}</strong>
            )}
          </Form.Item>
        </Col>
        <Col xs={24} lg={12}>
          <Form.Item
            label="Email"
            name="email"
            initialValue={customer.email}
            rules={[{ pattern: EMAIL_REGEX, required: true, message: "Please enter a valid email address" }]}
          >
            {mode.edit ? (
              <Input
                type="email"
                placeholder="Enter email"
                value={customer.email}
                onChange={({ target: { value } }) => handleChange("email", value)}
              />
            ) : (
              <strong>{customer.email}</strong>
            )}
          </Form.Item>
        </Col>
        <Col xs={24} lg={12}>
          <Form.Item
            label="Phone number"
            name="phone"
            initialValue={customer.phone}
            rules={[
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  return validateAustralianPhoneNumber(_, value);
                },
                message: "Please enter a valid australian phone number"
              }
            ]}
          >
            {mode.edit ? (
              <Input
                type="tel"
                placeholder="Enter phone"
                value={customer.phone}
                onChange={({ target: { value } }) => handleChange("phone", value)}
              />
            ) : (
              <strong>{startCase(customer.phone)}</strong>
            )}
          </Form.Item>
        </Col>
        <Col xs={24} lg={12}>
          <Form.Item 
            label="Discount (%)" 
            name="discount" 
            initialValue={customer.discount}
            tooltip="Customer-specific discount percentage."
          >
            {mode.edit ? (
              <Input
                type="number"
                min={0}
                max={100}
                placeholder="Enter Discount"
                value={customer.discount || operator?.opts?.charter?.discount || 0}
                initialValue={operator?.opts?.charter?.discount || 0}
                onChange={({ target: { value } }) => {
                  const numValue = parseFloat(value);
                  if (!isNaN(numValue) && numValue >= 0 && numValue <= 100) {
                    setCustomerWith("discount", numValue);
                  } else if (value === '') {
                    setCustomerWith("discount", null);
                  }
                }}
                suffix="%"
              />
            ) : (
              <strong>{customer.discount !== null ? `${customer.discount}%` : `${operator?.opts?.charter?.discount || 0}%`}</strong>
            )}
          </Form.Item>
        </Col>
        <Col xs={24} lg={12}>
          <Form.Item 
            label="Invoice Payment Type" 
            name="invoicePaymentType" 
            initialValue={customer.invoicePaymentType}
            tooltip="Default invoice payment type for new bookings."
          >
            {/* Should be either in-advance or in-arrears */}
            {mode.edit ? (
              <Select
                placeholder="Select Invoice Payment Type"
                value={customer.invoicePaymentType}
                onSelect={(value) => setCustomerWith("invoicePaymentType", value)}
              >
                <Select.Option value="in-advance">In-Advance</Select.Option>
                <Select.Option value="in-arrears">In-Arrears</Select.Option>
              </Select>
            ) : (
              <strong>{customer.invoicePaymentType ? customer.invoicePaymentType === 'in-arrears' ? 'In-Arrears' : 'In-Advance' : '-'}</strong>
            )}
          </Form.Item>
        </Col>
        <Col xs={24} lg={12}>
          <Form.Item label="Billing Address" name="addressLine1" initialValue={customer.addressLine1}>
            {mode.edit ? (
              <>
                <Input
                  placeholder="Enter Address line 1"
                  value={customer.addressLine1}
                  onChange={({ target: { value } }) => setCustomerWith("addressLine1", value)}
                  className="address-line-1"
                />
                <Input
                  placeholder="Enter Address line 2"
                  value={customer.addressLine2}
                  onChange={({ target: { value } }) => setCustomerWith("addressLine2", value)}
                />
              </>
            ) : (
              <>
                <strong>{startCase(customer.addressLine1)}</strong><br/>
                <strong>{startCase(customer.addressLine2)}</strong>
              </>
            )}
          </Form.Item>
        </Col>
        <Col xs={24} lg={12}>
          <Form.Item label="Additional Information" name="note" initialValue={customer.note}>
            {mode.edit ? (
              <TextArea
                placeholder="Enter Additional Information"
                value={customer.note}
                onChange={({ target: { value } }) => setCustomerWith("note", value)}
                style={{height: 75}}
              />
            ) : (
              <strong><Paragraph>{startCase(customer.note)}</Paragraph></strong>
            )}
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};

export const CustomerAdditionalContactsCard = ({ mode, customer, setCustomerWith, form }) => {
  // Initialize form values when customer data changes
  useEffect(() => {
    if (mode.edit) {
      form.setFieldsValue({ additionalContacts: customer.additionalContacts });
      form.validateFields(); // Trigger validation to show errors for invalid fields
    }
  }, [customer, mode.edit, form]);

  const addContact = () => {
    const newContacts = [...(customer.additionalContacts || []), { name: "", email: "", phone: "" }];
    setCustomerWith("additionalContacts", newContacts);
    form.setFieldsValue({ additionalContacts: newContacts });
  };

  const removeContact = (index) => {
    const newContacts = customer.additionalContacts.filter((_, i) => i !== index);
    setCustomerWith("additionalContacts", newContacts);
    form.setFieldsValue({ additionalContacts: newContacts });
  };

  const updateContact = (index, propName, value) => {
    const newContacts = customer.additionalContacts.map((contact, i) => (i === index ? { ...contact, [propName]: value } : contact));
    setCustomerWith("additionalContacts", newContacts);
    form.setFieldsValue({ additionalContacts: newContacts });
  };

  return (
    <Card
      title="Additional Contacts"
      bordered={false}
      className="card-main card-info row-separator"
    >
      {mode.edit && (
        <Button type="primary" className="icon-button btn-sm card-btn" onClick={addContact} icon={<PlusOutlined/>}>Add Contact</Button>
      )}
      <Form form={form} layout="vertical">
        {(customer.additionalContacts || []).map((contact, index) => (
          <React.Fragment key={index}>
            <Row gutter={[10, 20]}>
              <Col xs={24} lg={8}>
                <Form.Item
                  label="Name"
                  name={['additionalContacts', index, 'name']}
                  initialValue={contact.name}
                  rules={[{ required: true, message: "Please enter the name" }]}
                >
                  {mode.edit ? (
                    <Input placeholder="Enter name" onChange={({ target: { value } }) => updateContact(index, "name", value)} />
                  ) : (
                    <strong>{startCase(contact.name)}</strong>
                  )}
                </Form.Item>
              </Col>
              <Col xs={24} lg={8}>
                <Form.Item
                  label="Email"
                  name={['additionalContacts', index, 'email']}
                  initialValue={contact.email}
                  rules={[{ required: true, pattern: EMAIL_REGEX, message: "Please enter a valid email address" }]}
                >
                  {mode.edit ? (
                    <Input placeholder="Enter email" onChange={({ target: { value } }) => updateContact(index, "email", value)} />
                  ) : (
                    <strong>{contact.email}</strong>
                  )}
                </Form.Item>
              </Col>
              <Col xs={24} lg={8}>
                <Form.Item
                  label="Phone number"
                  name={['additionalContacts', index, 'phone']}
                  initialValue={contact.phone}
                  rules={[{ validator: validateAustralianPhoneNumber, message: "Please enter a valid phone number" }]}
                >
                  {mode.edit ? (
                    <Input placeholder="Enter phone" onChange={({ target: { value } }) => updateContact(index, "phone", value)} />
                  ) : (
                    <strong>{startCase(contact.phone)}</strong>
                  )}
                </Form.Item>
              </Col>
              {mode.edit && (
                <Col xs={24} className={"align-content-end mb-4"}>
                  <Button type="danger" className="icon-button btn-sm card-btn btn-filled btn-error" style={{marginTop: -15}} icon={<DeleteOutlined/>} onClick={() => removeContact(index)}>Delete</Button>
                </Col>
              )}
            </Row>
          </React.Fragment>
        ))}
      </Form>
    </Card>
  );
};

export const CustomerQuoteSummaryCard = ({ customer, selectedStatuses, setSelectedStatuses, selectedTotal, setSelectedTotal }) => {
  const { filteredBookings } = useBooking();
  const [quoteSummary, setQuoteSummary] = useState({
    total: 0,
    byStatus: {}
  });

  useEffect(() => {
    if (filteredBookings && customer) {
      // Filter bookings for this customer
      const bookings = filteredBookings.filter(
        booking => booking.customerId === customer.customerId
      );
      
      // Initialize summary with zero values for all possible statuses
      const summary = {
        total: 0,
        byStatus: CHARTER_STATUS.reduce((acc, status) => {
          acc[status.value] = { count: 0, value: 0, label: status.label };
          return acc;
        }, {})
      };
      
      // Calculate totals by status
      bookings.forEach(charter => {
        let quoteValue = 0;
        
        // Calculate the total from quote line items
        if (charter.quoteLineItems && charter.quoteLineItems.length > 0) {
          const subtotal = charter.quoteLineItems.reduce((sum, item) => {
            // Calculate amount if not directly available
            const amount = item.amount || (item.unitPrice && item.quantity ? 
              item.unitPrice * item.quantity * (1 - (item.discount || 0) / 100) : 0);
            return sum + (amount || 0);
          }, 0);
          
          // Add 10% GST
          quoteValue = subtotal * 1.1;
        }
        
        // Update the summary
        if (charter.status && summary.byStatus[charter.status]) {
          summary.byStatus[charter.status].count += 1;
          summary.byStatus[charter.status].value += quoteValue;
          summary.total += quoteValue;
        }
      });
      
      setQuoteSummary(summary);
      
      // Recalculate selected total if statuses are selected
      if (Object.keys(selectedStatuses).length > 0) {
        const newTotal = Object.keys(selectedStatuses).reduce((sum, selectedStatus) => {
          return sum + (summary.byStatus[selectedStatus]?.value || 0);
        }, 0);
        setSelectedTotal(newTotal);
      }
    }
  }, [filteredBookings, customer, selectedStatuses, setSelectedTotal]);

  // Function to format currency values
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  // Toggle selection of a status card
  const toggleStatusSelection = (status, value) => {
    setSelectedStatuses(prev => {
      const newSelectedStatuses = { ...prev };
      
      if (newSelectedStatuses[status]) {
        // If already selected, deselect it
        delete newSelectedStatuses[status];
      } else {
        // If not selected, select it
        newSelectedStatuses[status] = true;
      }
      
      // Recalculate total based on all selected statuses
      const newTotal = Object.keys(newSelectedStatuses).reduce((sum, selectedStatus) => {
        return sum + (quoteSummary.byStatus[selectedStatus]?.value || 0);
      }, 0);
      
      setSelectedTotal(newTotal);
      return newSelectedStatuses;
    });
  };

  return (
    <Card
      title={
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span>Customer Value</span>
          <div style={{ marginLeft: 'auto', gap: '6px' }} className="d-flex align-items-center">
            {Object.keys(selectedStatuses).length > 0 && (
              <Button 
                type="primary" 
                className="icon-button btn-filled icon-10 btn-xs mr-2"
                onClick={() => {
                  setSelectedStatuses({});
                  setSelectedTotal(0);
                }}
              >
                <Close/> Clear Selection
              </Button>
            )}
            <span style={{ fontWeight: '500', color: '#7C7D7E', textTransform: 'uppercase', fontSize: 14 }}>TOTAL:</span>
            {formatCurrency(Object.keys(selectedStatuses).length > 0 ? selectedTotal : quoteSummary.total)}
          </div>
        </div>
      }
      bordered={false}
      className="card-main card-info"
      style={{ marginBottom: '20px' }}
    >
      <div className="quote-summary">
        <div style={{ marginBottom: '12px', color: '#7C7D7E', fontSize: '13px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Click on cards to select and calculate custom totals</span>
        </div>
        <div style={{ display: 'flex', overflowX: 'auto', gap: '14px' }}>
          {/* Display by status */}
          {Object.entries(quoteSummary.byStatus)
            .filter(([_, data]) => data.count > 0)
            .sort((a, b) => b[1].value - a[1].value) // Sort by value (highest first)
            .map(([status, data]) => (
              <div
                key={status}
                onClick={() => toggleStatusSelection(status, data.value)}
                style={{ 
                  flex: '0 0 auto',
                  minWidth: '180px',
                  borderLeft: '4px solid', 
                  borderLeftColor: getStatusColor(status),
                  borderTop: '1px solid #E8E5FD',
                  borderBottom: '1px solid #E8E5FD',
                  borderRight: '1px solid #E8E5FD',
                  padding: '10px 15px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  backgroundColor: selectedStatuses[status] ? '#F6F7FF' : 'transparent',
                  boxShadow: selectedStatuses[status] ? '0 2px 8px rgba(0, 0, 0, 0.1)' : 'none',
                  transition: 'background-color 0.2s, box-shadow 0.2s'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontWeight: '500', color: '#7C7D7E', textTransform: 'uppercase' }}>{data.label} ({data.count})</span>
                  {/* <span>{data.count} booking{data.count !== 1 ? 's' : ''}</span> */}
                </div>
                <div>
                  <Title level={3} style={{ margin: 0, color: '#666', fontSize: 26 }}>
                    {formatCurrency(data.value)}
                  </Title>
                </div>
              </div>
            ))}
            
          {Object.keys(quoteSummary.byStatus).filter(key => quoteSummary.byStatus[key].count > 0).length === 0 && (
            <div style={{ 
              padding: '15px', 
              textAlign: 'center', 
              width: '100%',
              color: '#939CAE'
            }}>
              No booking data to display
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

// Helper function to get color based on status
function getStatusColor(status) {
  switch (status) {
    case 'new': return '#1890ff'; // Blue
    case 'sent': return '#faad14'; // Warning (yellow)
    case 'accepted': return '#52c41a'; // Success (green)
    case 'scheduled': return '#722ed1'; // Purple
    case 'allocated': return '#eb2f96'; // Pink
    case 'completed': return '#13c2c2'; // Cyan
    case 'cancelled': return '#f5222d'; // Error (red)
    default: return '#8c8c8c'; // Default gray
  }
}

export const CustomerBookingsCard = ({ customer, selectedStatuses }) => {
  const history = useHistory();
  const { filteredBookings } = useBooking();
  const [customerBookings, setCustomerBookings] = useState([]);
  const [isDeleting, setIsDeleting] = useState({});
  const [isCloning, setIsCloning] = useState(false);
  const [groupedData, setGroupedData] = useState({});
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const { DATE_STRING } = require('../../model/schedule');

  useEffect(() => {
    if (filteredBookings && customer) {
      // Filter bookings for this customer
      let bookings = filteredBookings.filter(
        booking => booking.customerId === customer.customerId
      );
      
      // Apply status filter if any statuses are selected
      if (Object.keys(selectedStatuses).length > 0) {
        bookings = bookings.filter(booking => selectedStatuses[booking.status]);
      }
      
      setCustomerBookings(bookings);
      
      // Group bookings by date
      const grouped = bookings.reduce((acc, charter) => {
        const dates = charter.dates.length ? charter.dates : [charter.startDate];
        dates.forEach(date => {
          // if date is not a dayjs object, convert it
          const dateKey = date?.format ? date.format("YYYY-MM-DD") : null;
          if (!acc[dateKey]) {
            acc[dateKey] = [];
          }
          acc[dateKey].push(charter);
        });
        if (dates.length === 0) {
          if (!acc['No Date']) {
            acc['No Date'] = [];
          }
          acc['No Date'].push(charter);
        }
        return acc;
      }, {});

      // Sort bookings within each date by time
      Object.keys(grouped).forEach((date) => {
        grouped[date].sort((a, b) => {
          const dateA = dayjs(a.startDate, DATE_STRING);
          const dateB = dayjs(b.startDate, DATE_STRING);
          return dateA.diff(dateB);
        });
      });

      setGroupedData(grouped);
    }
  }, [filteredBookings, customer, selectedStatuses]);

  const handleClone = async (charter) => {
    setIsCloning(true);
    try {
      console.log('Copy charter', charter.charterId);
      const clonedCharter = charter.clone();
      clonedCharter.charterId = ulid();
      clonedCharter.name += '-COPY';
      clonedCharter.status = 'new';
      clonedCharter.createdAt = new Date().toISOString();
      clonedCharter.updatedAt = new Date().toISOString();
      // copy itinerary
      clonedCharter.itinerary = clonedCharter.itinerary.map(route => {
        route.routeId = ulid();
        return route;
      })
      await routeModelData.save(clonedCharter.itinerary);
      // copy duties
      clonedCharter.duties = clonedCharter.duties.map(duty => {
        duty.shiftBatId = ulid();
        return duty;
      })
      await shiftBatModelData.save(clonedCharter.duties);
      await charterModelData.save(clonedCharter)
      console.log('Charter saved.');
    } finally {
      setIsCloning(false);
    }
  };

  const handleDelete = async (charter) => {
    setIsDeleting((prev) => ({ ...prev, [charter.charterId]: true }));
    try {
      charterModelData.delete([charter.charterId, charter.itineraryId], true).then(() => {
        console.log('Charter deleted.');
        shiftBatModelData.delete(charter.duties.map(duty => duty.shiftBatId), true).then(() => {
          console.log(`Deleted ${charter.duties.length} duties. for charter $${charter.charterId}`);
        })
        setIsDeleting({ ...isDeleting, [charter.charterId]: false });
      }).then(() => {
        routeModelData.delete(charter.itinerary.map(route => route.routeId), true).then(() => {
          console.log(`Deleted ${charter.itinerary.length} routes. for charter $${charter.charterId}`);
        })
      });
    } finally {
      setIsDeleting((prev) => ({ ...prev, [charter.charterId]: false }));
    }
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: "30%",
    },
    {
      title: "Booking Reference",
      dataIndex: "bookingReference",
      key: "bookingReference",
      className: 'text-center',
      width: "15%",
      render: (bookingReference) => bookingReference || "-",
    },
    {
      title: "Quote",
      key: "quote",
      className: 'text-center',
      width: "15%",
      render: (_, charter) => {
        if (charter.quoteLineItems && charter.quoteLineItems.length > 0) {
          // Calculate total amount from all quote line items
          const subtotal = charter.quoteLineItems.reduce((sum, item) => sum + (item.amount || 0), 0);
          // Add 10% GST
          const totalWithGST = subtotal * 1.1;
          return totalWithGST ? `$${Math.round(totalWithGST).toLocaleString()}` : "-";
        }
        return "-";
      },
    },
    {
      title: "Customer",
      dataIndex: "customer",
      key: "customer",
      className: 'text-center',
      width: "15%",
      render: (customer) => customer?.name || "-",
    },
    {
      title: "Passengers",
      dataIndex: "passengerCount",
      key: "passengers",
      className: 'text-center',
      width: "15%",
      render: (passengerCount) => passengerCount ? `${passengerCount}` : "-",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      className: 'text-center',
      width: '9%',
      render: (_, charter) => (
        <div className="d-flex simple-tags w-100 justify-content-center">
          <FilterTags filterItems={filterItems} model={charter} />
        </div>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      className: 'text-center',
      width: "6%",
      render: (_, charter) => (
        <div className="d-flex justify-content-around">
          <LoaderButton
            className="btn-icon-control"
            size="sm"
            isLoading={isCloning}
            icon={<Copy />}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handleClone(charter);
            }}
          />
          <Popconfirm
            title="Delete this booking?"
            onConfirm={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handleDelete(charter);
            }}
            onCancel={(e) => e.stopPropagation()}
            okText="Yes"
            cancelText="No"
          >
            <LoaderButton className={`btn-icon-control btn-delete`} size="sm" isLoading={isDeleting[charter.charterId]} icon={<Trash />} onClick={(e) => e.stopPropagation()} />
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div className="booking-list-container" style={{height: 'auto', overflow: 'visible'}}>
      <div className="page-title-wrap">
        <Title level={2} className="page-subtitle">
          Bookings
          {Object.keys(selectedStatuses).length > 0 && (
            <span style={{ 
              fontSize: '14px',
              fontWeight: 'normal',
              marginLeft: '8px', 
              color: '#1890ff',
              backgroundColor: 'rgba(24, 144, 255, 0.1)',
              padding: '4px 8px',
              borderRadius: '4px'
            }}>
              Filtered by selected status
            </span>
          )}
        </Title>
        <Button 
          type="primary" 
          className="icon-button btn-sm" 
          icon={<PlusOutlined />} 
          onClick={() => history.push(`/bookings/_new_?date=${dayjs().format('YYYY-MM-DD')}&customerId=${customer.customerId}`)}
        >
          Add Booking
        </Button>
      </div>
    {/* <Card
      title={
        <Flex justify="space-between">
          <div>Bookings</div>
          <Button 
            type="primary" 
            className="icon-button btn-sm" 
            icon={<PlusOutlined />} 
            onClick={() => history.push(`/bookings/_new_?date=${dayjs().format('YYYY-MM-DD')}&customerId=${customer.customerId}`)}
          >
            Add Booking
          </Button>
        </Flex>
      }
      bordered={false}
      className="card-main card-info row-separator"
      styles={{ body: { padding: 10 } }}
    > */}
      {Object.keys(groupedData).length > 0 ? (
        <div className="booking-list-content" 
          style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: '14px',
            padding: 0,
            overflow: 'visible'
          }}
        >
          {Object.keys(groupedData)
            .filter(date => date !== 'No Date')
            .sort((a, b) => dayjs(a).diff(dayjs(b)))
            .map(dateKey => (
              <div 
                key={dateKey} 
                id={`date-group-${dateKey}`}
                data-date={dateKey}
                className="date-group"
              >
                <Title level={5} style={{ fontWeight: 600, fontSize: 16, color: '#939CAE', padding: '6px 15px', marginBottom: '8px' }}>
                  {dayjs(dateKey).format("dddd, Do MMMM")}
                </Title>
                <div className="CardTable">
                  {groupedData[dateKey] && groupedData[dateKey].length > 0 ? (
                    <>
                      <Table
                        size="middle"
                        key={`booking-${dateKey}`}
                        columns={columns}
                        dataSource={groupedData[dateKey]}
                        pagination={false}
                        virtual={true}
                        onRow={(booking) => ({
                          onClick: (e) => {
                            if (e.ctrlKey || e.metaKey) {
                              window.open(`/bookings/${booking.charterId}`, "_blank");
                            } else {
                              history.push(`/bookings/${booking.charterId}`);
                            }
                          },
                        })}
                        rowKey={(charter) => charter.charterId}
                        rowClassName={() => "booking-row"}
                      />
                      <div className="d-flex justify-content-center align-items-end" style={{minHeight: '33px'}}>
                        <Button
                          type="primary"
                          className="icon-button btn-sm mb-1"
                          icon={<PlusOutlined />}
                          onClick={() => history.push(`/bookings/_new_?date=${dateKey}&customerId=${customer.customerId}`)}>
                            Add Booking
                        </Button>
                      </div>
                    </>
                  ) : (
                    <div className="no-bookings-message">
                      <div className="no-bookings-message-add-button">
                        <span>Bookings available</span>
                        <Button
                          type="primary"
                          className="icon-button btn-sm"
                          icon={<PlusOutlined />}
                          onClick={() => history.push(`/bookings/_new_?date=${dateKey}&customerId=${customer.customerId}`)}>
                            Add Booking
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          
          {/* Handle "No Date" bookings if they exist */}
          {groupedData['No Date'] && groupedData['No Date'].length > 0 && (
            <div 
              id="date-group-No-Date"
              data-date="No-Date"
              className="date-group"
            >
              <Title level={5} style={{ fontWeight: 600, fontSize: 16, color: '#939CAE', padding: '6px 15px', marginBottom: '8px' }}>
                No Date
              </Title>
              <div className="CardTable mb-4">
                <Table
                  size="middle"
                  key="booking-No-Date"
                  columns={columns}
                  dataSource={groupedData['No Date']}
                  pagination={false}
                  virtual={true}
                  showHeader={false}
                  onRow={(booking) => ({
                    onClick: (e) => {
                      if (e.ctrlKey || e.metaKey) {
                        window.open(`/bookings/${booking.charterId}`, "_blank");
                      } else {
                        history.push(`/bookings/${booking.charterId}`);
                      }
                    },
                  })}
                  rowKey={(charter) => charter.charterId}
                  rowClassName={() => "booking-row"}
                />
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="no-bookings-message" style={{ 
          padding: '20px', 
          textAlign: 'center', 
          backgroundColor: '#f5f5f5', 
          borderRadius: '8px',
          color: '#939CAE'
        }}>
          No bookings found for this customer
        </div>
      )}
    </div>
  );
};

function CustomerDetails({ menu = true, edit = false, onSave }) {
  const {operator} = useAppContext()
  const {
    model: customer,
    setModel: setCustomer,
    initialModelRef: initialCustomerRef,
    mode,
    setMode,
    controls,
  } = useModel({
    modelService: customerModelData,
    initialInstance: new Customer({ customerId: "_" }),
    edit,
    onSave,
  });

  const [isValid, setIsValid] = useState(false);
  const [selectedStatuses, setSelectedStatuses] = useState({});
  const [selectedTotal, setSelectedTotal] = useState(0);

  const [form] = Form.useForm();
  const [contactsForm] = Form.useForm();

  const setCustomerWith = useCallback(
    (propName, value) => {
      if (!Array.isArray(propName)) {
        propName = [propName];
        value = [value];
      }
      setCustomer((customer) => {
        const newCustomer = new Customer({ ...customer });
        propName.forEach((prop, idx) => {
          if (value[idx] === undefined || value[idx] === null) {
            delete newCustomer[prop];
            return;
          }
          newCustomer[prop] = value[idx];
        });
        return newCustomer;
      });
    },
    [setCustomer]
  );

  const handleSave = () => {
    customerModelData.save(customer).then(() => {
      setMode({ edit: false });
      form.resetFields();
    });
  };

  useEffect(() => {
    if (mode.edit) {
      Promise.all([form.validateFields(), contactsForm.validateFields()])
        .then((s) => {
          setIsValid(true);
        })
        .catch((e) => {
          setIsValid(false);
        });
    }
  }, [mode.edit, form, contactsForm, customer]);

  return (
    <div className="customer-details w-secondary-menu">
      {customer ? (
        <>
          <SaveMenu
            save={handleSave}
            editMode={mode.edit}
            id={customer.customerId}
            setEditMode={(edit) => setMode({ edit })}
            controls={controls}
            updated={controls.canUndo}
            modelInstance={customer}
            initialInstanceRef={initialCustomerRef}
            listUrl={`/customers`}
            modelService={customerModelData}
            closeView={true}
          />
          <Row gutter={[20, 20]} className="w-100">
            {!mode.overview && !menu && (
              <CustomerMenu
                customer={customer}
                activeKey={"1"}
                setCustomer={setCustomer}
                disabled={{
                  customer: false,
                  itinerary: mode.edit,
                  quote: mode.edit,
                }}
                hide={{
                  customer: false,
                  itinerary: customer.customerId === "_",
                  quote: customer.customerId === "_",
                }}
              />
            )}
            <Col xs={24} lg={!mode.overview && !menu ? 19 : 24}>
              <Row gutter={[20, 20]}>
                <Col xs={24}>
                  <CustomerQuoteSummaryCard 
                    customer={customer} 
                    selectedStatuses={selectedStatuses}
                    setSelectedStatuses={setSelectedStatuses}
                    selectedTotal={selectedTotal}
                    setSelectedTotal={setSelectedTotal}
                  />
                </Col>
                <Col xs={12}>
                  <Card title={<Flex justify="space-between"><div>Customer Details</div></Flex>} bordered={false} className="card-main card-info">
                    <CustomerDetailsCard mode={mode} customer={customer} setCustomerWith={setCustomerWith} form={form} operator={operator}/>
                  </Card>
                </Col>
                <Col xs={12}>
                  <CustomerAdditionalContactsCard mode={mode} customer={customer} setCustomerWith={setCustomerWith} form={contactsForm} />
                </Col>
                <Col xs={24}>
                  <CustomerBookingsCard customer={customer} selectedStatuses={selectedStatuses} />
                </Col>
              </Row>
            </Col>
          </Row>
        </>
      ) : (
        <LoadMessage message="Loading Customer Details..." />
      )}
    </div>
  );
}

export default React.memo(CustomerDetails);
